import { DateValue } from '@internationalized/date';
import { Spacing } from '@persona-id/design-tokens';
import { DatabaseIcon } from '@persona-id/design-tokens/icons';
import { RangeValue } from '@react-types/shared';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import AnalyticsDatePicker from 'dashboard/components/AnalyticsDatePicker';
import { AnalyticsDateRange } from 'dashboard/components/AnalyticsDatePicker/AnalyticsDatePicker';
import DisabledProductPage from 'dashboard/components/DisabledProductPage';
import { DISABLED_REPORTS_ANALYTICS_PAGE } from 'dashboard/components/DisabledProductPage/config';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import LoadingOverlay from 'dashboard/components/LoadingOverlay';
import PersonaErrorBoundary from 'dashboard/components/PersonaErrorBoundary';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import { Container, Header } from 'dashboard/layouts/IndexViewLayoutV2';
import { Period } from 'dashboard/lib/unixTime';
import { ReportTemplate } from 'dashboard/models/report-template';
import { Tag } from 'dashboard/models/tag';
import {
  ReportsGranularities,
  ReportsMetricsFilterParams,
  ReportsMetricsState,
  ReportTemplateToken,
} from 'dashboard/redux/reducers/metrics-reports';
import ReportMetrics from 'dashboard/views/DashboardReportsAnalyticsView/ReportMetrics';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { Label } from 'design-system/components/Text';
import { captureMessage } from 'lib/reporting';
import { Iso8601DateString } from 'lib/types';

export interface DashboardReportsAnalyticsViewProps
  extends Pick<ReportsMetricsState, 'volumeSummary' | 'reportsOverTime' | 'lastQueryFilters'> {
  filters: ReportsMetricsFilterParams;
  productFeatureIsDisabled: boolean;
  templates: ReportTemplate[];
  handleTemplateFilterUpdate: (templateIds: string[]) => void;
  handleTagFilterUpdate: (tagIds: string[]) => void;
  handleUpdateGranularity: (
    templateIds: ReportTemplateToken[],
    templateId: ReportTemplateToken,
    period: Period,
  ) => void;
  granularities: ReportsGranularities;
  loading: boolean;
  queriedAt: Iso8601DateString;
  updatedAt: Iso8601DateString;
  loadMetrics: (templateIds: string[]) => Promise<void>;
  error: boolean;
  handleDateUpdateV2: (range: RangeValue<DateValue> | null) => void;
  tags?: Tag[];
}

const StyledCard = styled(Card)`
  margin-top: ${Spacing[4]};
  margin-left: ${Spacing[6]};
  margin-right: ${Spacing[6]};
`;

const RunButton = styled(Button)`
  height: ${Spacing[6]};
`;

const RunContainer = styled.div`
  flex: none;
  display: grid;
  grid-gap: ${Spacing[2]};
  align-items: flex-end;
  justify-items: flex-end;
`;

const SearchAndFilterCardBody = styled(Card.Body)`
  grid-gap: 0;
  grid-template-columns: 1fr auto;
  padding-top: 0px;

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const SearchAndFilterCardTitle = styled(Card.Title)`
  padding-top: ${Spacing[4]};
  padding-left: ${Spacing[5]};
`;

const SearchAndFilterContainer = styled.div`
  max-width: 1000px;
  display: grid;
  align-items: end;
  text-overflow: ellipsis;
  grid-template-columns: minmax(300px, 2fr) 2fr 1fr;
  grid-gap: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const StyledInfoIcon = styled(DatabaseIcon)`
  margin-bottom: -${Spacing[1]};
`;

const DashboardReportsAnalyticsView = ({
  filters,
  templates,
  loading,
  handleDateUpdateV2,
  handleUpdateGranularity,
  handleTemplateFilterUpdate,
  handleTagFilterUpdate,
  volumeSummary,
  reportsOverTime,
  granularities,
  loadMetrics,
  queriedAt,
  updatedAt,
  productFeatureIsDisabled,
  lastQueryFilters,
  error,
  tags = [],
}: DashboardReportsAnalyticsViewProps) => {
  const { createdAtStart, createdAtEnd, templateIds, tagIds } = filters;
  const dateRange = useMemo<AnalyticsDateRange>(() => {
    return {
      startDate: createdAtStart ? DateTime.fromISO(createdAtStart, { setZone: true }) : undefined,
      endDate: createdAtEnd ? DateTime.fromISO(createdAtEnd, { setZone: true }) : undefined,
    };
  }, [createdAtStart, createdAtEnd]);

  const templateFilterOptions = useMemo(
    () =>
      templates.map((template) => ({
        label: template.attributes.name,
        value: template.id,
      })),
    [templates],
  );

  const templateFilterLabelTextMapper = useLabelTextMapper(templateFilterOptions, 'templates', {
    emptyIsAll: true,
  });

  const tagFilterOptions = useMemo(
    () =>
      tags.map((tag) => ({
        label: tag.attributes.name,
        value: tag.id,
      })),
    [tags],
  );

  const tagFilterLabelTextMapper = useLabelTextMapper(tagFilterOptions, 'tags', {
    emptyIsAll: true,
  });

  // On an initial page click, we want to load metrics
  const loaded = queriedAt != null;

  useEffect(() => {
    if (!productFeatureIsDisabled && !loaded && !loading && !error) {
      loadMetrics(templateIds);
    }
  }, [templateIds, loading, dateRange, productFeatureIsDisabled, loaded, granularities, error]);

  if (productFeatureIsDisabled) {
    return (
      <DisabledProductPage
        id={DISABLED_REPORTS_ANALYTICS_PAGE.id}
        title={DISABLED_REPORTS_ANALYTICS_PAGE.title}
        headerTitle={DISABLED_REPORTS_ANALYTICS_PAGE.headerTitle}
        description={DISABLED_REPORTS_ANALYTICS_PAGE.description}
        uses={DISABLED_REPORTS_ANALYTICS_PAGE.uses}
        image={DISABLED_REPORTS_ANALYTICS_PAGE.image}
      />
    );
  }

  return (
    <>
      <Container>
        <InactiveEnvironmentBanner />
        <Header title='Analytics' />
      </Container>
      <StyledCard>
        <SearchAndFilterCardTitle>Query</SearchAndFilterCardTitle>
        <SearchAndFilterCardBody>
          <SearchAndFilterContainer>
            <AnalyticsDatePicker
              dataTest='reports-analytics-view__date-range'
              handleDatesChangeV2={handleDateUpdateV2}
              dateRange={dateRange}
              disabled={loading}
            />
            {templates.length > 1 && (
              <div>
                <FormControlLabel>Report templates</FormControlLabel>
                <MultiSelect
                  value={templateIds}
                  selectOptionValues={templateFilterOptions}
                  labelTextMapper={templateFilterLabelTextMapper}
                  onChange={handleTemplateFilterUpdate}
                  disabled={loading}
                  size='md'
                  data-test='metrics-checks-view__select-template'
                />
              </div>
            )}
            {tags.length > 0 && (
              <div>
                <FormControlLabel>Report tags</FormControlLabel>
                <MultiSelect
                  value={tagIds}
                  selectOptionValues={tagFilterOptions}
                  labelTextMapper={tagFilterLabelTextMapper}
                  onChange={handleTagFilterUpdate}
                  disabled={loading}
                  size='md'
                  data-test='metrics-checks-view__select-tag'
                />
              </div>
            )}
          </SearchAndFilterContainer>
          <RunContainer>
            {queriedAt != null && (
              <Label size='md' color='tertiary' weight='bold'>
                Last ran {DateTime.fromISO(queriedAt).toRelative()}{' '}
                {updatedAt != null && (
                  <HelpIcon icon={<StyledInfoIcon />}>
                    Data is up to date as of {DateTime.fromSQL(updatedAt).toRelative()}
                  </HelpIcon>
                )}
              </Label>
            )}
            <RunButton
              id='reports-analytics-view__run'
              onClick={() => loadMetrics(templateIds)}
              isPending={loading}
              disabled={loading}
              width='auto'
              size='sm'
            >
              Run
            </RunButton>
          </RunContainer>
        </SearchAndFilterCardBody>
      </StyledCard>
      {loading && <LoadingOverlay />}
      {queriedAt != null && (
        <PersonaErrorBoundary
          onCatch={(error) =>
            captureMessage('Report Analytics failed to render', 'error', {
              error,
              component: 'DashboardReportAnalyticsView',
            })
          }
        >
          <ReportMetrics
            loading={loading}
            queriedAt={queriedAt}
            volumeSummary={volumeSummary}
            reportsOverTime={reportsOverTime}
            granularities={granularities}
            handleUpdateGranularity={handleUpdateGranularity}
            templateIds={templateIds as ReportTemplateToken[]}
            lastQueryFilters={lastQueryFilters}
          />
        </PersonaErrorBoundary>
      )}
    </>
  );
};

export default DashboardReportsAnalyticsView;
